/**
 * UOM data clients — the reference data the display-conversion pipeline needs:
 *
 *   • Oxbo-P  GET /v1/meta/field-units            — field → quantity class
 *   • Oxbo-M  GET /v1/uom/profiles                — the pickable profiles
 *   • Oxbo-M  GET /v1/uom/profiles/{key}/resolved — dimension → unit + ABCD
 *   • Oxbo-M  GET /v1/uom/org/effective           — org default profile/currency
 *   • Oxbo-M  GET /v1/fx/rate?base&quote&as_of     — effective-dated FX
 *
 * Read-through + heavily cached (this is slow-moving reference data). The M
 * calls reuse the same Bearer token as `mApi` (prod-login token, or dev-minted
 * token; none in a mocks build). In standalone mode MSW serves all of it.
 */
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getDevToken } from "@api/devAuth";
import { getProdToken } from "@api/prodAuth";
import type { FieldUnitsMap, ResolvedProfile, ProfileSummary, FxRate } from "./types";

function getAuthToken(): string | null {
  return getProdToken() ?? getDevToken();
}

const M_BASE_URL = import.meta.env.VITE_OXBO_M_URL ?? "https://m.dev.oxbo.energy";
const P_BASE_URL = import.meta.env.VITE_OXBO_P_URL ?? "https://p.dev.oxbo.energy";

/** Org default profile/currency — `GET /v1/uom/org/effective`. */
export interface OrgEffectiveUom {
  profile_key: string | null;
  display_currency: string | null;
}

export const unitsApi = createApi({
  reducerPath: "unitsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/",
    // Attach the Oxbo-M Bearer token ONLY to Oxbo-M endpoints. The field-units
    // registry lives on Oxbo-P, whose dev JWKS verifier rejects ANY presented
    // token (it accepts a no-token request as the synthetic dev user) — so
    // sending the M token to P would 401 the field-units load. Gate by endpoint.
    prepareHeaders: (headers, { endpoint }) => {
      if (endpoint === "getFieldUnits") return headers; // Oxbo-P: no token
      const token = getAuthToken();
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  // Reference data changes rarely — cache generously (5 min unused retention).
  keepUnusedDataFor: 300,
  tagTypes: ["FieldUnits", "Profile", "ProfileList", "OrgEffective", "Fx"],
  endpoints: (builder) => ({
    // The field → quantity-class registry (Oxbo-P).
    getFieldUnits: builder.query<FieldUnitsMap, void>({
      query: () => `${P_BASE_URL}/v1/meta/field-units`,
      providesTags: ["FieldUnits"],
    }),
    // The list of pickable profiles (Oxbo-M) — feeds the Settings picker.
    getProfiles: builder.query<ProfileSummary[], void>({
      query: () => `${M_BASE_URL}/v1/uom/profiles`,
      providesTags: ["ProfileList"],
    }),
    // A single profile fully resolved to units + ABCD coefficients (Oxbo-M).
    getResolvedProfile: builder.query<ResolvedProfile, string>({
      query: (key) => `${M_BASE_URL}/v1/uom/profiles/${encodeURIComponent(key)}/resolved`,
      providesTags: (_r, _e, key) => [{ type: "Profile", id: key }],
    }),
    // The org's effective default profile + display currency (Oxbo-M). Returns a
    // 4xx when nothing is configured — callers treat that as "no org default".
    getOrgEffective: builder.query<OrgEffectiveUom, { operationRef?: string } | void>({
      query: (arg) => ({
        url: `${M_BASE_URL}/v1/uom/org/effective`,
        params: arg && arg.operationRef ? { operation_ref: arg.operationRef } : {},
      }),
      providesTags: ["OrgEffective"],
    }),
    // An effective-dated FX rate (Oxbo-M). `asOf` = the value's own date.
    getFxRate: builder.query<FxRate, { base: string; quote: string; asOf: string }>({
      query: ({ base, quote, asOf }) => ({
        url: `${M_BASE_URL}/v1/fx/rate`,
        params: { base, quote, as_of: asOf },
      }),
      providesTags: (_r, _e, { base, quote }) => [{ type: "Fx", id: `${base}/${quote}` }],
    }),
  }),
});

export const {
  useGetFieldUnitsQuery,
  useGetProfilesQuery,
  useGetResolvedProfileQuery,
  useGetOrgEffectiveQuery,
  useGetFxRateQuery,
} = unitsApi;
